/**
 * Token de sesión del editor para el API (Authorization: Bearer en manager/id y manager/update).
 *
 * El editor se carga en un iframe de app.leadcars, en otro origen, y no tiene el token del
 * cliente. Se lo pide a la página padre por postMessage y esta le devuelve un token que el API
 * emite solo para esta plantilla y que caduca a los 30 minutos (EmailEditorTokenService en
 * api.leadcars.es). Por eso se pide uno nuevo en cada llamada: al cargar y al guardar.
 * Mismo protocolo en app.leadcars (assets/js/pages/13-templates/editor-advanced.js).
 */
const TOKEN_REQUEST = "lc:email-editor:token-request";
const TOKEN_RESPONSE = "lc:email-editor:token";
const RETRY_MS = 500;
const TIMEOUT_MS = 10000;

const configuredOrigins = (import.meta.env.VITE_LC_APP_ORIGINS ?? "")
  .split(",")
  .map((origin) => origin.trim())
  .filter(Boolean);

/**
 * Orígenes de los que se acepta el token: los de VITE_LC_APP_ORIGINS (separados por comas) o, si
 * no se define, cualquier subdominio https de leadcars.es (app, appdev, apptest, beta, partners...)
 * y, en desarrollo, localhost.
 *
 * @param {string} origin
 * @returns {boolean}
 */
export function isAllowedParentOrigin(origin) {
  if (configuredOrigins.length > 0) {
    return configuredOrigins.includes(origin);
  }
  if (/^https:\/\/([a-z0-9-]+\.)*leadcars\.es$/.test(origin)) {
    return true;
  }

  return (
    import.meta.env.DEV === true &&
    /^https?:\/\/(localhost|127\.0\.0\.1|host\.docker\.internal)(:\d+)?$/.test(origin)
  );
}

/**
 * Pide un token a la página padre.
 *
 * @returns {Promise<string>}
 */
export function requestEditorToken() {
  return new Promise((resolve, reject) => {
    if (window.parent === window) {
      reject(new Error("El editor solo funciona dentro de LeadCars"));
      return;
    }

    // Cada petición lleva su id y el padre lo devuelve: así no se toma como propia la respuesta
    // rezagada a un reintento de una petición anterior
    const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    let retry;
    let timeout;
    const finish = () => {
      clearInterval(retry);
      clearTimeout(timeout);
      window.removeEventListener("message", onMessage);
    };
    const onMessage = (event) => {
      if (
        event.source !== window.parent ||
        !isAllowedParentOrigin(event.origin) ||
        event.data?.type !== TOKEN_RESPONSE ||
        event.data.id !== id
      ) {
        return;
      }
      finish();
      if (typeof event.data.token === "string" && event.data.token !== "") {
        resolve(event.data.token);
      } else {
        reject(new Error("LeadCars no ha podido autorizar el editor"));
      }
    };
    // La petición no lleva datos, así que puede ir a cualquier origen: lo que se valida es el
    // origen de la respuesta, que es la que trae el token.
    const ask = () => window.parent.postMessage({ type: TOKEN_REQUEST, id }, "*");

    window.addEventListener("message", onMessage);
    ask();
    // La página padre puede no estar escuchando todavía cuando arranca el editor
    retry = setInterval(ask, RETRY_MS);
    timeout = setTimeout(() => {
      finish();
      reject(new Error("LeadCars no ha respondido a la petición de token del editor"));
    }, TIMEOUT_MS);
  });
}
